"use client"

import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
} from "@mui/material"
import { Menu as MenuIcon } from "@mui/icons-material"
import { useEffect, useState } from "react"
import { useUser } from "@/contexts/UserContext";
import logo from "../assets/logo.png"; 
import { LogOutIcon, UserIcon } from "lucide-react";

export default function MainHeader() {
  const { user } = useUser();
  const muiTheme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false)
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down("lg"));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const isAdminOrOrg = user?.is_admin || user?.is_org;
  const [displayName, setDisplatName] = useState("");

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isMobile) {
      event.stopPropagation(); // Prevent event from bubbling up and closing drawer
    }
    document.body.classList.remove("ro-popover-open");
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMoreMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoreMenuAnchor(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setMoreMenuAnchor(null);
  };

  const navItems = ["Home", "EduCatalyst", "EduCommunity", "ThoughtCoCreate", "EduMarketplace", "Support"]

  const getMobileNavLink = (item: string) => {
    switch (item) {
      case "Home":
        return `https://${process.env.API_HOST}`;
      case "EduCatalyst":
        return `https://${process.env.API_HOST}/educatalyst`;
      case "EduCommunity":
        return `https://${process.env.API_HOST}/activity-feed`;
      case "ThoughtCoCreate":
        return `https://${process.env.API_HOST}/thoughtcocreate`;
      case "EduMarketplace":
        return "#"; // Keep disabled for now
      case "Support":
        return `${process.env.URL_COMMUNITY_GUILINE}`;
      default:
        return "#";
    }
  }

   useEffect(() => {
     setDisplatName(user?.display_name || "")
   }, [user]);

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: "center" }}>
      <Typography
        variant="h6"
        component="a"
        href="#"
        sx={{
          my: 2,
          fontSize: "1.5rem",
          fontWeight: 900,
          color: "text.primary",
          textDecoration: "none",
          display: "block",
        }}
      >
        <img
          src={logo || "/placeholder.svg"}
          alt="logo"
          style={{
            width: isMobile ? "140px" : "180px",
            marginRight: "10px",
            cursor: "pointer",
          }}
          onClick={() => (window.location.href = `https://pythaverse.net/`)}
        />
      </Typography>
      <Divider />
      <List>
        {navItems.map((item) => {
          const link = getMobileNavLink(item);
          return (
            <ListItem key={item} disablePadding>
              <ListItemButton
                sx={{ textAlign: "center" }}
                disabled={item === "EduMarketplace"}
                href={link} // Thêm href vào đây
                component="a" // Đảm bảo ListItemButton hoạt động như một thẻ a
              >
                <ListItemText primary={item} />
              </ListItemButton>
            </ListItem>
          )
        })}
        <ListItem disablePadding>
          <ListItemButton sx={{ textAlign: "center" }} href={user?.login_url} component="a">
            <ListItemText primary="Sign In" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            sx={{
              textAlign: "center",
              bgcolor: "primary.main",
              color: "white",
              my: 1,
              mx: 2,
              borderRadius: 1,
              "&:hover": {
                bgcolor: "primary.dark",
              },
            }}
            href={`https://${process.env.API_HOST}/${process.env.PREFIX_EDUCATOR_REGISTER}`}
            component="a"
          >
            <ListItemText primary="Join for Free" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  )

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: "white",
        boxShadow: "0 1px 0 #e0e0e0",
        py: { xs: 0.5, md: 1 },
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ justifyContent: "space-between" }}>
          <Typography
            variant="h6"
            component="a"
            href="#"
            sx={{
              fontSize: "1.8rem",
              fontWeight: 900,
              color: "text.primary",
              textDecoration: "none",
            }}
          >
            <img
              src={logo || "/placeholder.svg"}
              alt="logo"
              style={{
                width: isMobile ? "140px" : isTablet ? "160px" : "180px",
                marginRight: "10px",
                cursor: "pointer",
              }}
              onClick={() => (window.location.href = `https://pythaverse.net/`)}
            />
          </Typography>

          <Box sx={{ display: { xs: "none", lg: "flex" }, gap: 2 }}>
            <Button 
              color="inherit" 
              sx={{ 
                color: "text.secondary", 
                fontWeight: 600, 
                fontSize: "0.875rem",
                minWidth: "auto",
                px: 1.5
              }} 
              href={`https://${process.env.API_HOST}`}
            >
              Home
            </Button>
            <Button 
              color="inherit" 
              sx={{ 
                color: "text.secondary", 
                fontWeight: 600, 
                fontSize: "0.875rem",
                minWidth: "auto",
                px: 1.5
              }} 
              href={`https://${process.env.API_HOST}/educatalyst`}
            >
              EduCatalyst
            </Button>
            <Button 
              color="inherit" 
              sx={{ 
                color: "text.secondary", 
                fontWeight: 600, 
                fontSize: "0.875rem",
                minWidth: "auto",
                px: 1.5
              }} 
              href={`https://${process.env.API_HOST}/activity-feed`}
            >
              EduCommunity
            </Button>
            <Button 
              color="inherit" 
              sx={{ 
                color: "text.secondary", 
                fontWeight: 600, 
                fontSize: "0.875rem",
                minWidth: "auto",
                px: 1.5
              }} 
              href={`https://${process.env.API_HOST}/thoughtcocreate`}
            >
              ThoughtCoCreate
            </Button>
            <Button 
              disabled 
              color="inherit" 
              sx={{ 
                color: "text.secondary", 
                fontWeight: 600, 
                fontSize: "0.875rem",
                minWidth: "auto",
                px: 1.5
              }}
            >
              EduMarketplace
            </Button>
            <Button 
              color="inherit" 
              sx={{ 
                color: "text.secondary", 
                fontWeight: 600, 
                fontSize: "0.875rem",
                minWidth: "auto",
                px: 1.5
              }} 
              href={`${process.env.URL_COMMUNITY_GUILINE}`} 
              target="_blank"
            >
              Support
            </Button>
          </Box>

          {/* Medium screens navigation */}
          <Box sx={{ display: { xs: "none", md: "flex", lg: "none" }, gap: 1 }}>
            <Button 
              color="inherit" 
              sx={{ 
                color: "text.secondary", 
                fontWeight: 600, 
                fontSize: "0.875rem",
                minWidth: "auto",
                px: 1
              }} 
              href={`https://${process.env.API_HOST}`}
            >
              Home
            </Button>
            <Button 
              color="inherit" 
              sx={{ 
                color: "text.secondary", 
                fontWeight: 600, 
                fontSize: "0.875rem",
                minWidth: "auto",
                px: 1
              }} 
              href={`https://${process.env.API_HOST}/educatalyst`}
            >
              EduCatalyst
            </Button>
            <Button 
              color="inherit" 
              sx={{ 
                color: "text.secondary", 
                fontWeight: 600, 
                fontSize: "0.875rem",
                minWidth: "auto",
                px: 1
              }} 
              onClick={handleMoreMenuClick}
            >
              More ▼
            </Button>
          </Box>

          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1 }}>
            {user?.user_login == null ||
              typeof user?.user_login == "undefined" ? (
              <>
                <Button
                  color="inherit"
                  href={user?.login_url}
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "background.paper",
                      transform: "none",
                    },
                  }}
                >
                  Sign In
                </Button>
                <Button variant="contained" color="primary" href={`https://${process.env.API_HOST}/${process.env.PREFIX_EDUCATOR_REGISTER}`}>
                  Join for Free
                </Button>
              </>
            ) : (
              <Button
                fullWidth
                variant="contained"
                onClick={handleUserMenuClick}
                color="primary"
                title={displayName}
                sx={{
                  textTransform: "none",
                  "& .MuiTypography-root": {
                    color: "white",
                  },
                  fontFamily: "Roboto",
                  fontStyle: "normal",
                  fontWeight: 700,
                  fontSize: "16px",
                  lineHeight: "19px",
                  // maxWidth: '200px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                }}
              >
                <Typography variant="h6" noWrap component="div" ml={1}>
                  {displayName}
                </Typography>
              </Button>
            )}
          </Box>

          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: "none" }, color: "text.primary" }}
          >
            <MenuIcon />
          </IconButton>

          {/* More menu for medium screens */}
          <Menu
            anchorEl={moreMenuAnchor}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            open={Boolean(moreMenuAnchor)}
            onClose={handleMoreMenuClose}
          >
            <MenuItem onClick={() => { window.location.href = `https://${process.env.API_HOST}/activity-feed`; handleMoreMenuClose(); }}>
              EduCommunity
            </MenuItem>
            <MenuItem onClick={() => { window.location.href = `https://${process.env.API_HOST}/thoughtcocreate`; handleMoreMenuClose(); }}>
              ThoughtCoCreate
            </MenuItem>
            <MenuItem disabled onClick={handleMoreMenuClose}>
              EduMarketplace
            </MenuItem>
            <MenuItem onClick={() => { window.open(`${process.env.URL_COMMUNITY_GUILINE}`, '_blank'); handleMoreMenuClose(); }}>
              Support
            </MenuItem>
          </Menu>

          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
          >
            {isAdminOrOrg ? (
              <MenuItem
                onClick={() =>
                  (window.location.href = `https://${process.env.API_HOST}/${process.env.PREFIX_WORKSHOP_ADMIN}`)
                }
              >
                <UserIcon size={20} />
                <Typography variant="inherit" noWrap component="span" ml={1}>
                  Admin dashboard
                </Typography>
              </MenuItem>
            ) : user?.role == "educator" ? (
              <MenuItem
                onClick={() =>
                  (window.location.href = `https://${process.env.API_HOST}/${process.env.PREFIX}`)
                }
              >
                <UserIcon size={20} />
                <Typography variant="inherit" noWrap component="span" ml={1}>
                  Educator Workspace
                </Typography>
              </MenuItem>
            ) : (
              <></>
            )}

            <Divider />
            <MenuItem
              onClick={() => (window.location.href = user?.logout_url || "/")}
            >
              <LogOutIcon size={20} />
              <Typography variant="inherit" noWrap component="span" ml={1}>
                Logout
              </Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </Container>

      <Box component="nav">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: 280 },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
    </AppBar>
  )
}